import type { Dispatch, SetStateAction } from 'react'
import type {
  PaginatedTableReducerAction,
  PaginatedTableReducerState,
  SetRowDataPayload,
  ToggleRowPayload,
} from './PaginatedTableReducer.d'
import type { JobsListParams } from '@contracts'
import type { ExtractElementType } from 'utils/utils.d'

export enum PaginatedTableActionTypes {
  SET_IS_LOADING = 'SET_IS_LOADING',
  SET_POSITION = 'SET_POSITION',
  SET_ROW_DATA = 'SET_ROW_DATA',
  SET_TOTAL_ITEM_COUNT = 'SET_TOTAL_ITEM_COUNT',
  SET_SORTING_DETAILS = 'SET_SORTING_DETAILS',
  TOGGLE_ROW_SELECTION = 'TOGGLE_ROW_SELECTION',
  CLEAR_ROW_SELECTION = 'CLEAR_ROW_SELECTION',
}

export const setIsTaskLoading = (payload: boolean): PaginatedTableReducerAction<any> => ({
  type: PaginatedTableActionTypes.SET_IS_LOADING,
  payload,
})

export const setTaskPosition = (payload: number): PaginatedTableReducerAction<any> => ({
  type: PaginatedTableActionTypes.SET_POSITION,
  payload,
})

export function setRowData<T>(payload: SetRowDataPayload<T>): PaginatedTableReducerAction<T> {
  return {
    type: PaginatedTableActionTypes.SET_ROW_DATA,
    payload,
  }
}

export const setTotalItemCount = (payload: number): PaginatedTableReducerAction<any> => ({
  type: PaginatedTableActionTypes.SET_TOTAL_ITEM_COUNT,
  payload,
})

export function toggleRowSelection<T extends { readonly _id?: string }>(
  payload: ToggleRowPayload,
): PaginatedTableReducerAction<T> {
  return { type: PaginatedTableActionTypes.TOGGLE_ROW_SELECTION, payload }
}

export function clearRowSelection(): PaginatedTableReducerAction<any> {
  return { type: PaginatedTableActionTypes.CLEAR_ROW_SELECTION, payload: null }
}

export function getPaginatedTableReducerEmptyState<T>(): PaginatedTableReducerState<T> {
  return {
    isLoading: true,
    position: 0,
    itemCount: 0,
    totalItemCount: 0,
    rowData: [],
    selectedRowIds: [],
    approvalRowIds: [],
  }
}

export function paginatedTableReducer<T>(state: PaginatedTableReducerState<T>, action: PaginatedTableReducerAction<T>) {
  const { type, payload } = action
  switch (type) {
    case PaginatedTableActionTypes.SET_IS_LOADING: {
      return {
        ...state,
        isLoading: payload,
      }
    }
    case PaginatedTableActionTypes.SET_POSITION: {
      return {
        ...state,
        position: payload,
      }
    }
    case PaginatedTableActionTypes.SET_TOTAL_ITEM_COUNT: {
      return {
        ...state,
        totalItemCount: payload,
      }
    }
    case PaginatedTableActionTypes.SET_ROW_DATA: {
      const { shouldAppend, rowData } = payload
      const newRowData = shouldAppend ? [...state.rowData, ...rowData] : rowData
      return {
        ...state,
        rowData: newRowData,
        itemCount: newRowData.length,
      }
    }
    case PaginatedTableActionTypes.TOGGLE_ROW_SELECTION: {
      const { id, isChecked, status } = payload as ToggleRowPayload

      if (!state.rowData || !state.selectedRowIds || !state.approvalRowIds) return state
      const newRowData = Array.from(state.rowData) as ((T & { _id: string }) | null)[]
      const rowData = newRowData.map(row => {
        return row && row?._id === id ? { ...row, is_checked: isChecked } : row
      })
      const newSelectedRowIds = Array.from(state.selectedRowIds)
      const newApprovalRowIds = Array.from(state.approvalRowIds)
      if (isChecked) {
        if (!newSelectedRowIds.includes(id)) newSelectedRowIds.push(id)
        if (
          (status as ExtractElementType<JobsListParams['statuses']>) === 'ready_for_review' &&
          !newApprovalRowIds.includes(id)
        )
          newApprovalRowIds.push(id)
      } else {
        if (!newSelectedRowIds.includes(id)) return state
        const selectedIdx = newSelectedRowIds.findIndex(selectedId => selectedId === id)
        newSelectedRowIds.splice(selectedIdx, 1)
        if (newApprovalRowIds.includes(id)) {
          const approvalIdx = newApprovalRowIds.findIndex(approvalId => approvalId === id)
          newApprovalRowIds.splice(approvalIdx, 1)
        }
      }
      const returnObject = {
        ...state,
        rowData: rowData as T[],
        selectedRowIds: newSelectedRowIds,
        approvalRowIds: newApprovalRowIds,
      } as PaginatedTableReducerState<T>
      return returnObject
    }
    case PaginatedTableActionTypes.CLEAR_ROW_SELECTION: {
      if (!state.rowData || !state.selectedRowIds || !state.approvalRowIds) return state
      const newRowData = Array.from(state.rowData) as ((T & { _id: string }) | null)[]
      const rowData = newRowData.map(row => {
        return row && { ...row, is_checked: false }
      })

      const returnObject = {
        ...state,
        rowData: rowData as T[],
        selectedRowIds: [],
        approvalRowIds: [],
      } as PaginatedTableReducerState<T>
      return returnObject
    }
    default: {
      return state
    }
  }
}

// this function is used to set the position of the paginated table and call the new url
// set new position for api  and then call setUrl (will call the api in the background)
export const setPosition = (url: string | URL, setUrl: Dispatch<SetStateAction<URL>>, position: number = 0) => {
  const newUrl = new URL(url)
  newUrl.searchParams.set('position', position.toString())
  setUrl(newUrl)
}
